import { compose } from "redux";
import classNames from "classnames";
import { connect } from "react-redux";
import React, { PureComponent } from "react";
import Person from "@material-ui/icons/Person";
import { withStyles } from "@material-ui/core/styles";
import toJSComponent from "with-immutable-props-to-js";
import { reduxForm, reset } from "redux-form/immutable";
import ArrowForward from "@material-ui/icons/ArrowForward";
import { green, red, yellow } from "@material-ui/core/colors";
import { Avatar, Button, Grid, Typography } from "@material-ui/core";

import { LoadingDataState } from "altus-datastate";

import {
  getGroupFromState,
  getQualificationByCardIdFromState,
  getDeploymentDetailByCardIdFromState,
} from "features/planner/planner.selectors";

import { getEmployeesProjectsCardIdFromState } from "features/planner/employee/employee.selectors";

import {
  EMPLOYEE_TYPES,
  GROUP_FLAG_TYPES,
  CREATE_CARD_MOVE_FORM,
} from "features/planner/planner.constants";

import {
  employeeStayOnLoad,
  createOrUpdateEmployeeStayOnSubmit,
  assignProjectToEmployee,
  unassignProjectToEmployee,
} from "features/planner/employee/employee.actions";

import { getInitials } from "utils/app.util";
import BasePage from "app/components/BasePage";
import LoadingButton from "app/components/LoadingButton";
import { getActionDataStateFromState } from "app/app.selectors";
import OilRigIcon from "features/planner/components/OilRigIcon";
import NoContentBasePage from "app/components/NoContentBasePage";
import { ACTIONS } from "features/planner/employee/employee.constants";
import { getEmployeeCardFromState } from "features/planner/employee/employee.selectors";
import DeployOffshoreForm from "features/planner/components/CreateCardMove/DeployOffshoreForm";

class EmployeeStayContainer extends PureComponent {
  componentDidMount() {
    const { dispatchOnLoad, employeeCardId, employeeCard } = this.props;

    dispatchOnLoad(employeeCardId, employeeCard.groupId);
  }

  assignProjectToUser = projectId => {
    const { dispatchAssignProject, employeeCard } = this.props;
    dispatchAssignProject(employeeCard.id, employeeCard.groupId, projectId);
  };

  unassignProjectToUser = projectId => {
    const { dispatchUnassignProject, employeeCard } = this.props;
    dispatchUnassignProject(employeeCard.id, employeeCard.groupId, projectId);
  };

  render() {
    const {
      Icon,
      group,
      dirty,
      classes,
      onReset,
      groupId,
      dataState,
      breadcrumb,
      submitting,
      employeeCard,
      handleSubmit,
      deploymentDetail,
      employeesProjects,
    } = this.props;

    if (dataState.isLoading()) return null;

    if (!deploymentDetail) {
      const header =
        employeeCard.employee.type === EMPLOYEE_TYPES.EMPLOYEE
          ? "Employee not deployed"
          : "Sub contractor not deployed";

      return (
        <NoContentBasePage
          Icon={Person}
          title={breadcrumb}
          header={header}
          description={`Since ${employeeCard.name} is not currently deployed offshore, there are no Stay details to display.`}
        />
      );
    }

    return (
      <BasePage Icon={Icon} title={breadcrumb} dataState={dataState}>
        <Grid container item xs={12} md={10} lg={8} xl={6}>
          <Grid
            item
            container
            justifyContent="center"
            alignItems="center"
            wrap="nowrap"
            className={classes.cardsRoot}
          >
            <Grid
              item
              xs={4}
              container
              spacing={2}
              wrap="nowrap"
              alignItems="center"
              className={classes.card}
            >
              <Grid item>
                <Avatar>{getInitials(employeeCard.name)}</Avatar>
              </Grid>
              <Grid item zeroMinWidth>
                <Typography color="inherit" noWrap>
                  {employeeCard.name}
                </Typography>
              </Grid>
            </Grid>
            <Grid container item xs={2} justifyContent="center" wrap="nowrap">
              <ArrowForward color="action" />
              <ArrowForward color="action" />
              <ArrowForward color="action" />
            </Grid>
            <Grid
              item
              xs={4}
              container
              spacing={2}
              alignItems="center"
              wrap="nowrap"
              className={classes.group}
            >
              <Grid item>
                <OilRigIcon
                  classes={{
                    root: classNames(
                      classes.groupIcon,
                      classes[group.flagType],
                    ),
                  }}
                />
              </Grid>
              <Grid item zeroMinWidth>
                <Typography color="inherit">{group.name}</Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <DeployOffshoreForm
              employeeProjects={employeesProjects}
              displayFromDate
              groupId={groupId}
              onSubmit={handleSubmit}
              onAssignProject={this.assignProjectToUser}
              onUnassignProject={this.unassignProjectToUser}
              isEmployeePage={true}
            />
          </Grid>
          <Grid
            item
            container
            spacing={2}
            justifyContent="flex-end"
            className={classes.actionsRoot}
          >
            <Grid item>
              <Button
                disabled={!dirty}
                size="large"
                variant="contained"
                onClick={onReset}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <LoadingButton
                loading={submitting}
                disabled={!dirty}
                color="primary"
                size="large"
                variant="contained"
                onClick={handleSubmit}
              >
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </BasePage>
    );
  }
}

const styles = theme => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(2),
  },
  cardsRoot: {
    backgroundColor: theme.palette.grey[200],
    maxHeight: 100,
    minHeight: 100,
    marginBottom: theme.spacing(2),
  },
  card: ({ qualification }) => ({
    background:
      qualification && qualification.backColor
        ? qualification.backColor
        : theme.altus.background.header,
    color:
      qualification && qualification.backColor
        ? theme.palette.getContrastText(qualification.backColor)
        : theme.palette.getContrastText(theme.altus.background.header),
  }),
  group: {
    background: theme.altus.background.header,
    color: theme.palette.getContrastText(theme.altus.background.header),
  },
  groupIcon: {
    display: "flex",
    width: 40,
    height: 40,
  },
  [GROUP_FLAG_TYPES.NONE]: {
    backgroundColor: theme.altus.background.header,
  },
  [GROUP_FLAG_TYPES.GREEN]: {
    backgroundColor: green[700],
  },
  [GROUP_FLAG_TYPES.YELLOW]: {
    backgroundColor: yellow[800],
  },
  [GROUP_FLAG_TYPES.RED]: {
    backgroundColor: red[800],
  },
  actionsRoot: {
    marginTop: theme.spacing(2),
  },
});

EmployeeStayContainer.defaultProps = {
  group: {},
  employeeCard: {},
  qualification: {},
  deploymentDetail: null,
  replaceEmployeeCard: {},
  dataState: LoadingDataState,
};

export default compose(
  connect(
    (state, { employeeCardId }) => {
      const employeeCard = getEmployeeCardFromState(state, employeeCardId);

      return {
        dataState: getActionDataStateFromState(
          state,
          ACTIONS.EMPLOYEE_STAY_ON_LOAD,
        ),
        deploymentDetail: getDeploymentDetailByCardIdFromState(
          state,
          employeeCardId,
        ),
        employeeCard,
        group: employeeCard
          ? getGroupFromState(state, employeeCard.get("groupId"))
          : undefined,
        groupId: employeeCard.get("groupId"),
        qualification: getQualificationByCardIdFromState(state, employeeCardId),
        employeesProjects: getEmployeesProjectsCardIdFromState(
          state,
          employeeCardId,
        ),
      };
    },
    {
      dispatchOnLoad: employeeStayOnLoad,
      onSubmit: createOrUpdateEmployeeStayOnSubmit,
      dispatchAssignProject: assignProjectToEmployee,
      dispatchUnassignProject: unassignProjectToEmployee,

      onReset: () => reset(CREATE_CARD_MOVE_FORM.ID),
    },
  ),
  reduxForm({ form: CREATE_CARD_MOVE_FORM.ID }),
  toJSComponent,
  withStyles(styles),
)(EmployeeStayContainer);
